import { Injectable, signal, computed, inject } from '@angular/core';
import { AppStorageService } from './app-storage.service';

/**
 * First-run tour service (GAP-FU-12).
 *
 * Drives the 5-step callout walker. State persists to
 * `localStorage('razor:tour:completed')`. Tours are keyed by role
 * (operator / risk / compliance / admin) so each operator sees a
 * tour tuned to their workflow.
 */
export interface TourStep {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  /** CSS selector for the element this callout points at. */
  readonly target?: string;
}

export interface Tour {
  readonly id: string;
  readonly role: 'operator' | 'risk' | 'compliance' | 'admin';
  readonly steps: readonly TourStep[];
}

const STORAGE_KEY = 'razor:tour:completed';

const DEFAULT_TOURS: readonly Tour[] = [
  {
    id: 'operator-default',
    role: 'operator',
    steps: [
      { id: 'navbar', title: 'Trading mode', body: 'The pill in the navbar tells you whether you are in PAPER or LIVE mode. Look for the red rule across the top when LIVE.', target: 'app-paper-trading-badge' },
      { id: 'cmdk', title: 'Command palette', body: 'Press ⌘K (or Ctrl+K) anywhere to jump between symbols, routes, and actions.', target: 'app-command-palette' },
      { id: 'sidebar', title: 'Recent', body: 'The sidebar surfaces your most recent routes — quick jump back to where you were.', target: 'app-sidebar-modern' },
      { id: 'help', title: 'Help', body: 'Press ? at any time to open the keyboard shortcuts overlay.', target: undefined },
      { id: 'logout', title: 'Logging out', body: 'Your session signs you out automatically after the configured idle window. Watch for the toast warning.', target: 'app-session-timeout-dialog' },
    ],
  },
];

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly appStorage = inject(AppStorageService);
  private readonly completedTours = signal<Set<string>>(new Set());
  private readonly _activeTour = signal<Tour | null>(null);
  private readonly _stepIndex = signal<number>(0);

  readonly activeTour = this._activeTour.asReadonly();
  readonly stepIndex = this._stepIndex.asReadonly();
  readonly currentStep = computed<TourStep | null>(() => {
    const tour = this._activeTour();
    if (!tour) return null;
    return tour.steps[this._stepIndex()] ?? null;
  });

  constructor() {
    this.loadCompleted();
  }

  /** Start the tour for the given role (no-op if already completed). */
  startIfFirstRun(role: Tour['role'] = 'operator'): void {
    const tour = DEFAULT_TOURS.find(t => t.role === role);
    if (!tour) return;
    if (this.completedTours().has(tour.id)) return;
    this._activeTour.set(tour);
    this._stepIndex.set(0);
  }

  /** Always start the tour, even if completed before (settings → "Replay tour"). */
  start(role: Tour['role'] = 'operator'): void {
    const tour = DEFAULT_TOURS.find(t => t.role === role);
    if (!tour) return;
    this._activeTour.set(tour);
    this._stepIndex.set(0);
  }

  next(): void {
    const tour = this._activeTour();
    if (!tour) return;
    const next = this._stepIndex() + 1;
    if (next >= tour.steps.length) {
      this.complete();
    } else {
      this._stepIndex.set(next);
    }
  }

  previous(): void {
    this._stepIndex.update(i => Math.max(0, i - 1));
  }

  skip(): void {
    this.complete();
  }

  private complete(): void {
    const tour = this._activeTour();
    if (tour) {
      this.completedTours.update(set => {
        const next = new Set(set);
        next.add(tour.id);
        return next;
      });
      this.persistCompleted();
    }
    this._activeTour.set(null);
    this._stepIndex.set(0);
  }

  private loadCompleted(): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves STORAGE_KEY and
    // the string[] shape (getJSON returns null on server / miss / corrupt).
    const parsed = this.appStorage.getJSON<string[]>(STORAGE_KEY);
    if (Array.isArray(parsed)) {
      this.completedTours.set(new Set(parsed));
    }
  }

  private persistCompleted(): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves the STORAGE_KEY key.
    this.appStorage.setJSON(STORAGE_KEY, Array.from(this.completedTours()));
  }
}
